import type {CmLogger} from "@cm/log"
import {Quaternion} from "@cm/math"
import ndarray, {NdArray} from "ndarray"
import {Matrix, Vector} from "@cm/template-nodes"

export function zeros(...dims: number[]): NdArray {
    let totalSz = 1
    for (let i = 0; i < dims.length; i++) {
        totalSz *= dims[i]
    }
    return ndarray(new Float32Array(totalSz).fill(0), dims)
}

export function eye(sz: number): NdArray {
    const m = ndarray(new Float32Array(sz * sz).fill(0), [sz, sz])
    for (let i = 0; i < sz; i++) {
        m.set(i, i, 1)
    }
    return m
}

export function ones(...dims: number[]): NdArray {
    let totalSz = 1
    for (let i = 0; i < dims.length; i++) {
        totalSz *= dims[i]
    }
    return ndarray(new Float32Array(totalSz).fill(1), dims)
}

export function toMatrix(arr: ArrayLike<number>, m: number, n: number) {
    return ndarray(new Float32Array(arr), [m, n])
}

export function slice1d(arr: Vector, lowAxis0: number, hiAxis0: number): Vector {
    return arr.hi(hiAxis0).lo(lowAxis0)
}

export function slice2d(arr: Matrix, lowAxis0: number, hiAxis0: number, lowAxis1: number, hiAxis1: number): Matrix {
    return arr.hi(hiAxis0, hiAxis1).lo(lowAxis0, lowAxis1)
}

export function matMul(A: Matrix, B: Matrix, out: Matrix) {
    const ni = A.shape[0]
    const nj = A.shape[1]
    const nk = B.shape[1]
    for (let i = 0; i < ni; i++) {
        for (let k = 0; k < nk; k++) {
            let tmp = 0
            for (let j = 0; j < nj; j++) {
                tmp += A.get(i, j) * B.get(j, k)
            }
            out.set(i, k, tmp)
        }
    }
}

// export function anglesToQuaternion(rx: number, ry: number, rz: number) {
//     let cos_a = Math.cos(rz);
//     let sin_a = Math.sin(rz);
//     let cos_b = Math.cos(ry);
//     let sin_b = Math.sin(ry);
//     let cos_c = Math.cos(rx);
//     let sin_c = Math.sin(rx);
//     let matrix: Matrix4 = new Matrix4;
//     matrix.elements[0] = cos_a*cos_b;
//     matrix.elements[1] = sin_a*cos_b;
//     matrix.elements[2] = -sin_b;
//     matrix.elements[4] = cos_a*sin_b*sin_c - sin_a*cos_c;
//     matrix.elements[5] = sin_a*sin_b*sin_c + cos_a*cos_c;
//     matrix.elements[6] = cos_b*sin_c;
//     matrix.elements[8] = cos_a*sin_b*cos_c + sin_a*sin_c;
//     matrix.elements[9] = sin_a*sin_b*cos_c - cos_a*sin_c;
//     matrix.elements[10] = cos_b*cos_c;
//     matrix.elements[15] = 1;
//     const {position, quaternion, scale} = matrix.decompose();
//     return quaternion;
// }

export function angleDegreesToQuaternion(rx: number, ry: number, rz: number) {
    rx *= Math.PI / 180
    ry *= Math.PI / 180
    rz *= Math.PI / 180
    const rotMag = Math.sqrt(rx * rx + ry * ry + rz * rz)
    if (rotMag > 0.0) {
        rx = rx / rotMag
        ry = ry / rotMag
        rz = rz / rotMag
        const qx = rx * Math.sin(rotMag / 2.0)
        const qy = ry * Math.sin(rotMag / 2.0)
        const qz = rz * Math.sin(rotMag / 2.0)
        const qw = Math.cos(rotMag / 2.0)
        return new Quaternion(qx, qy, qz, qw)
    } else {
        return new Quaternion(0, 0, 0, 1)
    }
}

export function quaternionToAngleDegrees(q: Quaternion) {
    let rx = 0,
        ry = 0,
        rz = 0
    if (q.w !== 1.0) {
        const angle = 2 * Math.acos(q.w)
        rx = (q.x / Math.sqrt(1 - q.w * q.w)) * angle * (180 / Math.PI)
        ry = (q.y / Math.sqrt(1 - q.w * q.w)) * angle * (180 / Math.PI)
        rz = (q.z / Math.sqrt(1 - q.w * q.w)) * angle * (180 / Math.PI)
    }
    return [rx, ry, rz] as const
}

export let mathUtilsLogger: CmLogger | undefined
